import { useTheme } from '@mui/material';
import { PropsWithChildren, ReactNode, useCallback } from 'react';
import { useNavigate } from 'react-router';

import { useAppDispatch, useAppSelector } from '@/hooks/redux';
import { useTranslate } from '@/hooks/useTranslate';

import { signOut } from '@/redux/auth/actions';
import { selectLoggedInUser } from '@/redux/auth/selectors';

import { ColorExtractFn } from '@/types/components/common';
import { ProgressDotsProps } from '@/types/components/data-display';

import { getDataTestId } from '@/utils/browser';

import {
  PageBackground,
  PageContent,
  PageLogo,
  PageNavBar,
  StyledProgress,
} from './Page.styled';
import {
  BackToLoginButton,
  ScrollablePageContent,
  UnifiedPageFormContainer,
} from './UnifiedPage.styled';

type UnifiedPageProps = {
  hasDarkBackground?: boolean;
  hasLogo?: boolean;
  progressProps?: ProgressDotsProps;
  hasBackToLoginButton?: boolean;
  navBarContent?: ReactNode;
  backgroundChildren?: ReactNode;
  bgColor?: ColorExtractFn;
  scrollable?: boolean;
} & PropsWithChildren;

export function UnifiedPage({
  hasDarkBackground,
  hasLogo,
  progressProps,
  hasBackToLoginButton,
  navBarContent,
  children,
  backgroundChildren,
  bgColor,
  scrollable,
}: UnifiedPageProps) {
  const user = useAppSelector(selectLoggedInUser);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const { t } = useTranslate();

  const backgroundColor = bgColor?.(theme);

  const onBackToLogin = useCallback(() => {
    if (!user) {
      navigate(-1);
    } else {
      dispatch(signOut());
    }
  }, [dispatch, navigate, user]);

  const ContentWrapper = scrollable ? ScrollablePageContent : PageContent;

  const showNavBar = navBarContent || hasBackToLoginButton;

  return (
    <PageBackground isDark={hasDarkBackground} bgColor={backgroundColor}>
      {backgroundChildren}
      <ContentWrapper>
        <UnifiedPageFormContainer>
          {hasLogo && <PageLogo />}
          {progressProps && (
            <StyledProgress isOnDark={hasDarkBackground} {...progressProps} />
          )}
          {children}
          {showNavBar && (
            <PageNavBar isDarkBg={hasDarkBackground}>
              {navBarContent ?? (
                <BackToLoginButton
                  {...getDataTestId(`page-action-back-to-login`)}
                  onClick={onBackToLogin}
                  variant="text"
                >
                  {t('layout.page.button.backToLogin')}
                </BackToLoginButton>
              )}
            </PageNavBar>
          )}
        </UnifiedPageFormContainer>
      </ContentWrapper>
    </PageBackground>
  );
}
